import { useContext, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { apigClient } from '../../../clientSideServices/aws';
import { uploadToS3 } from '../../../clientSideServices/session';
import PopupContainer from '../../Common/PopupContainer';
import { HubContext } from '../../HubContext';

const DUMMY_THUMBNAIL = '/images/slide_dummy.png'; // Place this image in your public/images folder

const SLImportPPTXPopup = ({ open, onClose, onConfirm }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [extracting, setExtracting] = useState(false);
    const [metadata, setMetadata] = useState(null);
    const [showSlideSelect, setShowSlideSelect] = useState(false);
    const [selectedSlides, setSelectedSlides] = useState([]);
    const [converting, setConverting] = useState(false);
    const [convertProgress, setConvertProgress] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [pptxKey, setPptxKey] = useState(null);
    const [previewImg, setPreviewImg] = useState(null);
    const hubCtx = useContext(HubContext);

    // Upload logic
    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file && file.name.endsWith('.pptx')) {
            if (file.size > 990 * 1024 * 1024) {
                alert('File size exceeds 990MB limit.');
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.pptx')) {
            if (file.size > 990 * 1024 * 1024) {
                alert('File size exceeds 990MB limit.');
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleClick = () => {
        if (!uploading && !showSlideSelect && !converting && !extracting) fileInputRef.current?.click();
    };

    // Uploads the PPTX and returns the S3 key
    const uploadPPTXToS3 = async (file, onProgress) => {
        const bucket = 'storiez-data';
        const dateStr = new Date().toISOString().slice(0, 10); // e.g., '2024-06-09'
        const uniqueId = uuidv4();
        const newFileName = uniqueId + '.pptx';
        const key = `pptconverter/${dateStr}/${uniqueId}/${newFileName}`;
        // Create a new File object with the new name (if in browser)
        let uploadFile = file;
        if (typeof File === 'function' && file instanceof File && file.name !== newFileName) {
            uploadFile = new File([file], newFileName, { type: file.type });
        }
        await uploadToS3(uploadFile, bucket, key, (progress) => onProgress(progress), false, file.type);
        return key;
    };

    // Poll for pptxmetadata.json
    const pollForMetadata = async (metadataUrl, retries = 150, delay = 2000) => {
        for (let i = 0; i < retries; i++) {
            try {
                const res = await fetch(metadataUrl);
                if (res.ok) {
                    const data = await res.json();
                    setMetadata(data);
                    setExtracting(false);
                    setShowSlideSelect(true);
                    setSelectedSlides(data.slides.map((s) => s.number));
                    return;
                }
            } catch (e) {
                // Suppress fetch errors (e.g., 403/404) while polling
            }
            await new Promise((r) => setTimeout(r, delay));
        }
        setExtracting(false);
        alert('Failed to extract slides. Please try again.');
    };

    const handleUploadConfirm = async () => {
        setUploading(true);
        setProgress(0);
        try {
            const key = await uploadPPTXToS3(selectedFile, (percent) => setProgress(percent));
            setUploading(false);
            setProgress(0);
            setExtracting(true);
            setPptxKey(key);
            // Compute the metadata URL based on the uploaded key
            const baseKey = key.replace(/\/[^/]+$/, '');
            const metadataUrl = `https://storiez-data.s3.amazonaws.com/${baseKey}/pptxmetadata.json`;
            pollForMetadata(metadataUrl);
        } catch (err) {
            setUploading(false);
            setProgress(0);
            alert('Upload failed. Please try again.');
        }
    };

    // Slide selection logic
    const handleSlideToggle = (number) => {
        setSelectedSlides((prev) =>
            prev.includes(number) ? prev.filter((sid) => sid !== number) : [...prev, number]
        );
    };
    const handleSelectAll = () => setSelectedSlides(metadata ? metadata.slides.map((s) => s.number) : []);
    const handleDeselectAll = () => setSelectedSlides([]);

    // Conversion logic
    const handleSlidesConfirm = async () => {
        setConverting(true);
        setConvertProgress(0);
        // Do NOT call onConfirm here
        const creator = hubCtx?.user?.id || '';
        const brandId = hubCtx?.brandId || '';
        const pptxPath = pptxKey;
        const slidesToConvert = selectedSlides;
        let completed = 0;
        const total = slidesToConvert.length;
        const updateProgress = () => {
            completed += 1;
            setConvertProgress((completed / total) * 100);
        };

        try {
            console.log('Calling apigClient...');
            const client = await apigClient('https://1767u9oiwk.execute-api.ap-southeast-1.amazonaws.com/prod');
            console.log('apigClient resolved:', client);
            await Promise.all(
                slidesToConvert.map(async (slideNumber) => {
                    const path = '/content/pptxtosceneconverter';
                    const body = {
                        path: pptxPath,
                        slideNumber: slideNumber - 1,
                        creator: creator,
                        brandId: brandId,
                    };
                    const res = await client.invokeApi(
                        {},
                        path,
                        'POST',
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        },
                        body
                    );
                    if (res.status !== 200) throw new Error(`Failed to convert slide ${slideNumber}`);
                    updateProgress();
                })
            );
            setConverting(false);
            setConvertProgress(0);
            setShowSlideSelect(false);
            setSelectedFile(null);
            setSelectedSlides([]);
            setMetadata(null);
            setPptxKey(null);
        } catch (err) {
            console.error('apigClient failed:', err);
            setConverting(false);
            setConvertProgress(0);
            alert('Conversion failed. Please try again.');
        }
    };

    // Reset on close
    const handleClose = () => {
        setSelectedFile(null);
        setUploading(false);
        setProgress(0);
        setShowSlideSelect(false);
        setSelectedSlides([]);
        setConverting(false);
        setConvertProgress(0);
        setExtracting(false);
        setMetadata(null);
        setPptxKey(null);
        onClose();
    };

    return (
        <PopupContainer maxWidth="80vw">
            <div className="pptx-popup-content">
                <div className="pptx-title-row">
                    <h2 className="pptx-title">Create Scenes from Powerpoint</h2>
                </div>
                {/* Upload Step */}
                {!showSlideSelect && !converting && !extracting && (
                    <>
                        <div
                            className="pptx-drop-area"
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onClick={handleClick}
                            style={{ cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1 }}
                        >
                            {selectedFile ? (
                                <div className="pptx-file-info">
                                    <span>{selectedFile.name}</span>
                                </div>
                            ) : (
                                <div className="pptx-drop-message">
                                    Drag 'n' drop, or click here to select files
                                </div>
                            )}
                            <input
                                type="file"
                                accept=".pptx"
                                style={{ display: 'none' }}
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                disabled={uploading}
                            />
                        </div>
                        {uploading && (
                            <div className="pptx-progress-bar">
                                <div className="pptx-progress" style={{ width: `${progress}%` }} />
                                <span className="pptx-progress-label">
                                    Uploading ({selectedFile?.name}), please wait...
                                </span>
                            </div>
                        )}
                        <div className="pptx-popup-actions">
                            <button className="btn btn-outline-dark" onClick={handleClose} disabled={uploading}>
                                Cancel
                            </button>
                            <button
                                className="btn btn-dark"
                                onClick={handleUploadConfirm}
                                disabled={!selectedFile || uploading}
                            >
                                Confirm
                            </button>
                        </div>
                        <div className="pptx-note">
                            Note: The PPTX conversion may take up to 20 minutes
                        </div>
                    </>
                )}
                {/* Extracting Step */}
                {extracting && (
                    <div className="pptx-extracting">
                        <div className="pptx-spinner-container">
                            <div className="pptx-spinner" />
                            <div className="pptx-spinner-label">Extracting Metadata, please wait...</div>
                        </div>
                        <div className="pptx-popup-actions">
                            <button className="btn btn-outline-dark" onClick={handleClose}>
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
                {/* Slide Selection Step */}
                {showSlideSelect && metadata && !converting && (
                    <>
                        <div className="pptx-slide-select-layout">
                            <div className="pptx-slide-sidebar">
                                <div className="pptx-slide-deck-label">{metadata.presentationTitle || 'Powerpoint Slide Deck'}</div>
                                <div className="pptx-slide-deck-thumb">
                                    {metadata.slides[0]?.thumbnailUrl && (
                                        <img src={metadata.slides[0].thumbnailUrl} alt="Deck thumbnail" style={{ width: '100%', borderRadius: 8 }} />
                                    )}
                                </div>
                                <button className="btn btn-outline-dark pptx-sidebar-btn" onClick={handleSelectAll}>
                                    Select All
                                </button>
                            </div>
                            <div className="pptx-slide-main">
                                <div className="pptx-slide-select-header">
                                    <span className="pptx-slide-count">{selectedSlides.length} Slide(s) Selected</span>
                                    <button className="btn btn-outline-dark pptx-sidebar-btn" onClick={handleDeselectAll}>
                                        Deselect All
                                    </button>
                                </div>
                                <div className="pptx-slide-grid">
                                    {metadata.slides.map((slide) => {
                                        const isSelected = selectedSlides.includes(slide.number);
                                        return (
                                            <div
                                                key={slide.number}
                                                className={`pptx-slide-item${isSelected ? ' selected' : ''}`}
                                                onClick={() => handleSlideToggle(slide.number)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <img
                                                    src={slide.thumbnailUrl ? slide.thumbnailUrl : DUMMY_THUMBNAIL}
                                                    alt={slide.title}
                                                    className="pptx-slide-thumb"
                                                    onClick={e => { e.stopPropagation(); setPreviewImg(slide.thumbnailUrl ? slide.thumbnailUrl : DUMMY_THUMBNAIL); }}
                                                    style={{ cursor: 'zoom-in' }}
                                                />
                                                <div className="pptx-slide-label">
                                                    {slide.title.length > 40 ? slide.title.slice(0, 40) + '…' : slide.title}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="pptx-popup-actions pptx-bottom-actions">
                            <button className="btn btn-outline-dark" onClick={handleClose}>
                                Cancel
                            </button>
                            <button
                                className="btn btn-dark"
                                onClick={handleSlidesConfirm}
                                disabled={selectedSlides.length === 0}
                            >
                                Convert
                            </button>
                        </div>
                        <div className="pptx-note pptx-bottom-note">
                            Note: The PPTX conversion may take up to 20 minutes
                        </div>
                    </>
                )}
                {/* Conversion Progress Step */}
                {converting && (
                    <div className="pptx-convert-progress" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                        <div style={{ fontSize: '1.3rem', fontWeight: 500, marginBottom: '32px', textAlign: 'center', maxWidth: 700 }}>
                            We are now generating your selected scenes. This process may take a few minutes and will continue in the background. You can return to the Scene overview to check when generation is complete. Once finished, your new scenes will appear there, and all digital assets will be added to your Asset Library.
                        </div>
                        <button className="btn btn-dark" style={{ borderRadius: 999, padding: '12px 48px', fontSize: '1.1rem' }} onClick={handleClose}>
                            Close
                        </button>
                    </div>
                )}
                {/* Image Preview Modal */}
                {previewImg && (
                    <div className="pptx-img-modal" onClick={() => setPreviewImg(null)}>
                        <img src={previewImg || DUMMY_THUMBNAIL} alt="Slide preview" className="pptx-img-modal-img" />
                    </div>
                )}
                <style jsx>{`
          .pptx-popup-content {
            width: 80vw;
            height: 80vh;
            max-width: 1920px;
            max-height: 90vh;
            background: #fff;
            border-radius: 18px;
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }
          .pptx-title-row {
            width: 100%;
            padding: 32px 40px 0 40px;
            box-sizing: border-box;
          }
          .pptx-title {
            font-size: 2rem;
            font-weight: 700;
            margin-bottom: 24px;
            text-align: left;
            letter-spacing: -1px;
          }
          .pptx-drop-area {
            border: 2px dashed #ccc;
            border-radius: 12px;
            padding: 48px;
            text-align: center;
            margin: 32px 0 24px 0;
            background: #fafafa;
            min-width: 350px;
          }
          .pptx-drop-message {
            color: #888;
            font-size: 18px;
          }
          .pptx-file-info {
            color: #333;
            font-size: 18px;
          }
          .pptx-popup-actions {
            display: flex;
            justify-content: flex-end;
            gap: 16px;
            margin-top: 32px;
          }
          .pptx-note {
            margin-top: 20px;
            color: #888;
            font-size: 15px;
            text-align: left;
          }
          .pptx-progress-bar {
            width: 100%;
            background: #eee;
            border-radius: 8px;
            margin: 32px 0 10px 0;
            height: 22px;
            position: relative;
          }
          .pptx-progress {
            background: #111;
            height: 100%;
            border-radius: 8px;
            transition: width 0.3s;
          }
          .pptx-progress-label {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            font-size: 15px;
            color: #fff;
            font-weight: bold;
            text-align: center;
          }
          .pptx-slide-select-layout {
            display: flex;
            flex-direction: row;
            gap: 32px;
            align-items: flex-start;
            min-height: 400px;
          }
          .pptx-slide-sidebar {
            min-width: 260px;
            max-width: 260px;
            display: flex;
            flex-direction: column;
            align-items: center;
            border-right: 1px solid #e0e0e0;
            padding-right: 32px;
          }
          .pptx-slide-deck-label {
            font-size: 1.1rem;
            color: #444;
            margin-bottom: 12px;
          }
          .pptx-slide-deck-thumb {
            width: 220px;
            height: 120px;
            background: #ccc;
            border-radius: 8px;
            margin-bottom: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }
          .pptx-sidebar-btn,
          .pptx-slide-select-header .btn {
            border-radius: 999px !important;
            padding: 8px 28px !important;
            font-size: 1rem;
          }
          .pptx-slide-main {
            flex: 1;
            display: flex;
            flex-direction: column;
          }
          .pptx-slide-select-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 18px;
          }
          .pptx-slide-count {
            font-size: 1.1rem;
            font-weight: 500;
          }
          .pptx-slide-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 18px;
            max-height: 55vh;
            overflow-y: auto;
          }
          .pptx-slide-item {
            background: #f3f3f3;
            border-radius: 8px;
            padding: 10px 4px 6px 4px;
            display: flex;
            flex-direction: column;
            align-items: center;
            box-shadow: 0 1px 4px rgba(0,0,0,0.04);
            min-width: 200px;
            position: relative;
            border: 2.5px solid transparent;
            transition: border 0.2s;
          }
          .pptx-slide-item.selected {
            border: 2.5px solid #111;
            box-shadow: 0 2px 8px rgba(0,0,0,0.10);
            background: #eaeaea;
          }
          .pptx-slide-item input[type='checkbox'] {
            width: 28px;
            height: 28px;
            accent-color: #111;
            margin-top: 8px;
            cursor: pointer;
            border-radius: 6px;
            border: 2px solid #111;
            box-shadow: 0 1px 2px rgba(0,0,0,0.08);
            transition: box-shadow 0.2s;
          }
          .pptx-slide-item input[type='checkbox']:checked {
            background-color: #111;
            border-color: #111;
          }
          .pptx-slide-item input[type='checkbox']:focus {
            outline: 2px solid #111;
          }
          .pptx-slide-thumb {
            width: 120px;
            height: 67.5px;
            background: #bbb;
            border-radius: 6px;
            margin-bottom: 6px;
            object-fit: cover;
            transition: box-shadow 0.2s, transform 0.2s;
          }
          .pptx-slide-thumb:hover {
            box-shadow: 0 4px 16px rgba(0,0,0,0.18);
            transform: scale(1.12);
            z-index: 2;
          }
          .pptx-slide-label {
            font-size: 14px;
            color: #444;
            margin-bottom: 4px;
            text-align: center;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 180px;
            min-height: 2.6em;
          }
          .pptx-popup-actions.pptx-bottom-actions {
            justify-content: flex-end;
            margin-top: 32px;
          }
          .pptx-note.pptx-bottom-note {
            margin-top: 32px;
            color: #888;
            font-size: 15px;
            text-align: left;
            border-top: 1px solid #eee;
            padding-top: 18px;
          }
          .pptx-convert-progress {
            margin: 48px 0;
          }
          .pptx-popup-actions button,
          .pptx-slide-select-actions button {
            border-radius: 999px !important;
            padding: 8px 28px !important;
            font-size: 1rem;
          }
          .pptx-popup-actions .btn-sm,
          .pptx-slide-select-actions .btn-sm {
            padding: 6px 18px !important;
            font-size: 0.95rem;
          }
          .pptx-slide-select-layout {
            flex: 1 1 auto;
            overflow-y: auto;
          }
          .pptx-slide-main {
            overflow-y: auto;
          }
          .pptx-img-modal {
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: rgba(0,0,0,0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            cursor: pointer;
          }
          .pptx-img-modal-img {
            max-width: 80vw;
            max-height: 80vh;
            border-radius: 12px;
            box-shadow: 0 8px 32px rgba(0,0,0,0.25);
            background: #fff;
          }
          .pptx-spinner-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 40vh;
          }
          .pptx-spinner {
            width: 64px;
            height: 64px;
            border: 8px solid #eee;
            border-top: 8px solid #111;
            border-radius: 50%;
            animation: pptx-spin 1s linear infinite;
            margin-bottom: 24px;
          }
          @keyframes pptx-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .pptx-spinner-label {
            color: #222;
            font-size: 1.2rem;
            font-weight: 500;
            text-align: center;
          }
        `}</style>
            </div>
        </PopupContainer>
    );
};

export default SLImportPPTXPopup; 